import React, { useEffect, useMemo, useState } from 'react';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Icon } from '@iconify/react'
import { usePrinter } from '../contexts/PrinterContext';
import { useAuth } from '../contexts/AuthContext';
import { printerNav } from '../components/NavigationPath';
import Loader from '../components/Loader';
import Layout2 from '../components/Layout2';
import AddPrinter from '../components/AddPrinter';
import PageHeader from '../components/PageHeader';

const Printers = () => {
  const { getAllPrinters, deletePrinter } = usePrinter();
  const { restaurantId, hasPermission, auth } = useAuth();

  const canCreate = useMemo(() => hasPermission("printers", "create"), [hasPermission]);
  const canUpdate = useMemo(() => hasPermission("printers", "update"), [hasPermission]);
  const canDelete = useMemo(() => hasPermission("printers", "delete"), [hasPermission]);
  const canAccessBackupPrinterIp = useMemo(() => hasPermission("backup-printer", "access"), [hasPermission]);

  const [printerList, setPrinterList] = useState([])
  const [visible, setVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editPrinterId, setEditPrinterId] = useState(null);
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState(-1);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const fetchPrinters = async () => {
    try {
      setIsLoading(true);
      const printers = await getAllPrinters(currentPage, rowsPerPage, sortField, sortOrder, null);
      if (printers.length !== 0) {
        setPrinterList(printers.data)
        setTotalRecords(printers.totalCount)
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching printers', error);
    }
  };

  useEffect(() => {
    if (restaurantId) {
      fetchPrinters();
    }
  }, [currentPage, rowsPerPage, sortField, sortOrder, restaurantId]);

  const handleDelete = async (id) => {
    try {
      const confirmed = await confirmDelete();

      if (confirmed) {
        await deletePrinter(id);
        fetchPrinters();
        handleClose();
      }
    } catch (error) {
      console.error('Error during delete operation', error);
    }
  }

  const confirmDelete = async () => {
    return new Promise((resolve) => {
      confirmDialog({
        message: 'Are you sure you want to delete this printer?',
        header: 'Delete Confirmation',
        icon: 'pi pi-info-circle',
        position: 'top',
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  };

  const handleClose = () => {
    setVisible(false);
    setEditMode(false);
    setEditPrinterId(null);
  };

  const handleSorting = async (e) => {
    const field = e.sortField;
    const order = e.sortOrder;

    setSortField(field);
    setSortOrder(order);
    fetchPrinters()
  };

  const onPageChange = (event) => {
    const newCurrentPage = Math.floor(event.first / event.rows) + 1;
    setCurrentPage(newCurrentPage);
    const newRowsPerPage = event.rows;
    setRowsPerPage(newRowsPerPage);
  };

  return (
    <Layout2 items={printerNav.items} navTitle={printerNav.header}>
      <div className="d-flex flex-column w-100">
        {
          <PageHeader
            title={'Printers'}
            buttonLable={canCreate ? 'Add New Printer' : ''}
            buttonOnClick={canCreate ? () => setVisible(true) : undefined}
          />
        }
        <div className='px-4 py-2'>
          <ConfirmDialog />
          {(visible || (editMode && editPrinterId)) && (
            <AddPrinter
              visible={visible}
              editMode={editMode}
              editPrinterId={editPrinterId}
              handleClose={handleClose}
              fetchPrinters={fetchPrinters}
              canAccessBackupPrinterIp={canAccessBackupPrinterIp}
            />
          )}
          {isLoading ? (
            <Loader />
          ) : (
            <DataTable
              className="customerTable thead-dark"
              totalRecords={totalRecords}
              lazy
              paginator
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={(e) => {
                if (!e.sortField && e.sortOrder === 0) {
                  handleSorting({ sortField: 'createdAt', sortOrder: -1 });
                } else {
                  handleSorting(e);
                }
              }}
              removableSort
              rows={rowsPerPage}
              value={printerList}
              first={(currentPage - 1) * rowsPerPage}
              onPage={onPageChange}
              dataKey="id"
              emptyMessage="No printers found."
            >
              <Column
                field="name"
                header="Name"
                sortable
                filterField="name"
                body={(rowData) => (
                  <div className='name' title={rowData.name}>
                    <Icon icon="pepicons-pencil:printer" style={{ color: 'black', marginRight: '4px' }} />
                    {rowData.name}
                  </div>
                )}
              />
              <Column field="type" header="Type" sortable filterField="type" />
              <Column field="ip_address" header="Printer IP" sortable filterField="ip_address" />
              {canAccessBackupPrinterIp && <Column field="backup_ip_address" header="Backup Printer IP" sortable filterField="backup_ip_address" />}
              <Column field="no_of_prints" header="No. Of Prints" sortable filterField="no_of_prints" />
              <Column
                field="send_to_both"
                header="Send To Both"
                sortable
                filterField="send_to_both"
                body={(rowData) => (
                  <>
                    {
                      rowData.send_to_both ? 'Yes' : 'No'
                    }
                  </>
                )}
              />
              {(canUpdate || canDelete) &&
                <Column
                  header="Action"
                  className='action_td'
                  align="left"
                  body={(rowData) => (
                    <>
                      {canUpdate && <i
                        className="pi pi-pencil pointerCursor"
                        title='Edit'
                        onClick={() => {
                          setEditPrinterId(rowData.id);
                          setEditMode(true)
                        }} />}
                      {
                        (rowData.name !== 'Admin' && canDelete) && (
                          <i className="pi pi-trash ms-2 pointerCursor" title='Delete' aria-label="Cancel" onClick={() => handleDelete(rowData.id)} />
                        )
                      }
                    </>
                  )}
                />
              }
            </DataTable>
          )}
        </div>
      </div>
    </Layout2>
  );
};

export default Printers;  
